<template>
  <div
    :class="ns.b('panel')"
    :style="panelStyle"
    data-test-name="transfer-panel"
    data-test-variant=""
    data-test-state=""
  >
    <div :class="ns.be('panel', 'header')">
      <b-checkbox
        v-model="allChecked"
        plain
        :indeterminate="isIndeterminate"
        @change="handleAllCheckedChange"
      >
        {{ title }}
      </b-checkbox>
      <span>{{ checkedSummary }}</span>
    </div>

    <b-input
      v-if="filterable"
      v-model="query"
      :class="ns.be('panel', 'filter')"
      :placeholder="placeholder"
      :prefix-icon="Search"
      clearable
      @mouseenter="inputHover = true"
      @mouseleave="inputHover = false"
    />

    <div :class="[ns.be('panel', 'body')]">
      <div :class="[ns.be('panel', 'body-inner')]">
        <b-checkbox-group
          v-show="!hasNoMatch && !isEmpty(data)"
          v-model="checked"
          vertical
          :class="[ns.is('filterable', filterable), ns.be('panel', 'list')]"
        >
          <b-checkbox
            v-for="item in filteredData"
            :key="item[propsAlias.key]"
            :class="ns.be('panel', 'item')"
            :value="item[propsAlias.key]"
            :disabled="item[propsAlias.disabled]"
            plain
          >
            <option-content :option="optionRender?.(item)" />
          </b-checkbox>
        </b-checkbox-group>

        <p
          v-show="hasNoMatch || isEmpty(data)"
          :class="ns.be('panel', 'empty')"
        >
          {{ hasNoMatch ? t('b.transfer.noMatch') : t('b.transfer.noData') }}
        </p>
      </div>
    </div>
    <div v-if="hasFooter" :class="ns.be('panel', 'footer')">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, toRefs, useSlots } from 'vue'
import { isEmpty } from '@bigin/utils'
import { useLocale, useNamespace } from '@bigin/hooks'
import { BCheckbox, BCheckboxGroup } from '@bigin/components/checkbox'
import { BInput } from '@bigin/components/input'
import { Search } from '@bigin/icons-vue'
import { transferPanelEmits, transferPanelProps } from './transfer-panel'
import { useCheck, usePropsAlias } from './composables'

import type { VNode } from 'vue'
import type { TransferPanelState } from './transfer-panel'

defineOptions({
  name: 'BTransferPanel',
})

const props = defineProps(transferPanelProps)
const emit = defineEmits(transferPanelEmits)
const slots = useSlots()

const OptionContent = ({ option }: { option: VNode | VNode[] }) => option

const { t } = useLocale()
const ns = useNamespace('transfer')

const panelState = reactive<TransferPanelState>({
  checked: [],
  allChecked: false,
  query: '',
  inputHover: false,
  checkChangeByUser: true,
})

const propsAlias = usePropsAlias(props)

const {
  filteredData,
  checkedSummary,
  isIndeterminate,
  handleAllCheckedChange,
} = useCheck(props, panelState, emit)

const hasNoMatch = computed(
  () => !isEmpty(panelState.query) && isEmpty(filteredData.value)
)

const hasFooter = computed(() => !isEmpty(slots.default!()[0].children))

const { checked, allChecked, query, inputHover } = toRefs(panelState)
const panelStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
}))

defineExpose({
  /** @description filter keyword */
  query,
})
</script>
